import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSafe } from '../context/SafeContext';
import { HbsButton } from '../components/hbs/Button';
import { ExportSafeModal } from '../components/ExportSafeModal';
import { RestoreSafeModal } from '../components/RestoreSafeModal';

/**
 * Data — the personal-data hub: import from another password manager, export an
 * encrypted backup, or restore from one. Import opens the full-page wizard
 * (`/import`); Export and Restore open their modals. These were gathered here
 * from the old "Tools" group (Import) and the Settings "Data" section
 * (Export / Restore).
 */
export default function DataPage(): JSX.Element {
  const navigate = useNavigate();
  const { items } = useSafe();
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);

  const personalItemCount = useMemo(() => items.filter((i) => !i.orgId).length, [items]);

  return (
    <div className="p-9 max-w-2xl mx-auto flex flex-col gap-8">
      <h1 className="font-display font-semibold text-[32px] tracking-hbs-tight text-hbs-text">
        Data
      </h1>

      {/* Import */}
      <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6">
        <h2 className="font-display text-[18px] text-hbs-text mb-4">Import</h2>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1">
              Import passwords
            </p>
            <p className="text-[13px] text-hbs-text-mute">
              Bring in your logins from LastPass, Chrome, Firefox, or a CSV spreadsheet.
            </p>
          </div>
          <HbsButton variant="subtle" id="open-import-button" onClick={() => navigate('/import')}>
            Import
          </HbsButton>
        </div>
      </section>

      {/* Backup — export + restore */}
      <section className="bg-hbs-surface border border-hbs-border rounded-hbs-xl p-6">
        <h2 className="font-display text-[18px] text-hbs-text mb-4">Backup</h2>
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1">
              Export your safe
            </p>
            <p className="text-[13px] text-hbs-text-mute">
              {personalItemCount > 0
                ? `Download an encrypted backup of your ${personalItemCount} item${personalItemCount === 1 ? '' : 's'} (including passkeys), or your logins as a CSV.`
                : 'Nothing in your safe to export yet.'}
            </p>
          </div>
          <HbsButton
            variant="subtle"
            id="export-logins-button"
            onClick={() => setExportModalOpen(true)}
            disabled={personalItemCount === 0}
          >
            Export
          </HbsButton>
        </div>
        <div className="border-t border-hbs-border mt-5 pt-5 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11.5px] font-bold uppercase tracking-hbs-overline text-hbs-text-mute mb-1">
              Restore from a backup
            </p>
            <p className="text-[13px] text-hbs-text-mute">
              Import an encrypted Hey Be Safe backup file. Its items — passkeys included — are added
              to your current safe.
            </p>
          </div>
          <HbsButton
            variant="subtle"
            id="restore-backup-button"
            onClick={() => setRestoreModalOpen(true)}
          >
            Restore
          </HbsButton>
        </div>
      </section>

      <ExportSafeModal open={exportModalOpen} onClose={() => setExportModalOpen(false)} />
      <RestoreSafeModal open={restoreModalOpen} onClose={() => setRestoreModalOpen(false)} />
    </div>
  );
}
